import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Mail } from 'lucide-react';
import Button from '../../ui/Button';
import nurseryAccountsService from '../../../services/nurseryAccounts';
import { errorMessage } from '../../../utils/apiResponse';
import { formatDateTimeInTz } from '../../../utils/timezone';
import type { InvoiceDetail } from '../../../types/nurseryAccounts';

/**
 * Whether the customer was emailed this invoice. Nothing in the app asks
 * before an invoice goes: once authorised it goes to Xero, and Xero emails it
 * by itself to an account that takes invoices by email and has an address
 * (HHNodeServer `_emailIfWanted`). This says which of those happened, and
 * why not, and lets an accounts seat ask Xero to email it now.
 */
export function InvoiceEmailCard({ invoice, tz, isAccounts, onSaved }: {
  invoice: InvoiceDetail;
  tz: string;
  isAccounts: boolean;
  onSaved: () => Promise<void> | void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  if (invoice.status === 'draft' || invoice.status === 'cancelled') return null;

  const inXero = invoice.xero_sync_status === 'synced';
  const status = invoice.email_status ?? null;
  const payerId = invoice.account?.payer_id ?? invoice.payer_id;

  const send = async () => {
    setBusy(true);
    setError(null);
    setDone(null);
    try {
      const res = await nurseryAccountsService.emailInvoice(invoice.id);
      if (res.success) {
        setDone('Xero has been asked to email it.');
        await onSaved();
      } else {
        setError(errorMessage(res, 'Could not email the invoice'));
      }
    } catch (e) {
      setError(errorMessage(e, 'Could not email the invoice'));
    }
    setBusy(false);
  };

  let tone = 'bg-white border-neutral-200';
  let text: ReactNode;
  let button: string | null = null;
  if (!invoice.xero_sync_status) {
    text = 'Xero is not connected, so nothing emails invoices. Download the PDF and send it yourself.';
  } else if (!inXero) {
    text = invoice.xero_sync_status === 'failed'
      ? 'Not emailed: it did not reach Xero. It is emailed once it gets there.'
      : 'On its way to Xero. Xero emails it once it gets there, if the account takes invoices by email.';
  } else if (status === 'sent') {
    text = <>Xero emailed it{invoice.email_to ? <> to <span className="font-medium">{invoice.email_to}</span></> : null}
      {invoice.emailed_at ? ` on ${formatDateTimeInTz(invoice.emailed_at, tz)}` : ''}.</>;
    button = 'Email again';
  } else if (status === 'not_wanted') {
    text = invoice.email_note === 'portal'
      ? 'Not emailed: this account takes its invoices through its own portal.'
      : 'Not emailed: this account is set not to be sent invoices.';
    button = 'Email it anyway';
  } else if (status === 'no_email') {
    tone = 'bg-amber-50 border-amber-200';
    text = <>Not emailed: the account has no email address.{' '}
      {payerId != null && <Link to={`/accounts/customers/${payerId}/edit`} className="underline font-medium">Add one</Link>}
      {payerId != null && ', then email it.'}</>;
    button = 'Email it';
  } else if (status === 'failed') {
    tone = 'bg-red-50 border-red-200';
    text = <>Xero did not email it: <span className="break-words">{invoice.email_note || 'it gave no reason'}</span>. Xero sends to the
      email address on its contact, so check that contact has one.</>;
    button = 'Try again';
  } else {
    text = 'In Xero. Whether Xero emailed it was not recorded (it went before this was kept).';
    button = 'Email it';
  }

  return (
    <div className={`border rounded-xl p-4 space-y-2 ${tone}`}>
      <div className="flex flex-wrap items-start gap-2">
        <Mail className="h-5 w-5 shrink-0 text-neutral-400" />
        <p className="flex-1 min-w-0 text-sm text-neutral-800 break-words">{text}</p>
        {isAccounts && inXero && button && (
          <Button variant="outline" size="sm" disabled={busy} onClick={send}>{busy ? 'Sending…' : button}</Button>
        )}
      </div>
      {done && <p className="text-sm text-green-800 break-words">{done}</p>}
      {error && <p className="text-sm text-red-700 break-words">{error}</p>}
    </div>
  );
}
